import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { ApiService } from '@app/core/services/api/api.service';
import {
  DashboardContentOptionsBuilder,
  FrameOptionsBuilder,
} from '@app/services/quicksight-analytics/quicksight-analytics.service';
import { ShopService } from '@app/services/shops/shop.service';
import { Shop } from '@pdqdev/pdq-shared/dist/entities/Shop';
import { createEmbeddingContext } from 'amazon-quicksight-embedding-sdk';
import { DashboardExperience } from 'amazon-quicksight-embedding-sdk';
import moment from 'moment';

import { Moment } from 'moment';
import { map, switchMap, takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { ToastService } from '@storybook/services/toast/toast.service';
import { PdqToastComponent } from '@app/storybook/components/toast/toast.component';
import { TimeUnits } from '@utils/times-utils';
import { Router } from '@angular/router';

@Component({
  selector: 'custom-dashboard',
  templateUrl: './custom-dashboard.component.html',
  styleUrls: ['./custom-dashboard.component.scss'],
  standalone: false,
})
export class CustomDashboardComponent implements OnInit, OnDestroy {
  notFoundMsg: string | null = null;
  constructor(
    private apiService: ApiService,
    private shopService: ShopService,
    private toastService: ToastService,
    private router: Router,
    private cdr: ChangeDetectorRef,
  ) {
    this.loading = true;
  }
  csvFile: File;
  startDate: Date;
  endDate: Date;
  minDate: Date;
  maxDate: Date;
  loading: boolean;
  csvIsBeingUploaded = false;
  shop: Shop;
  error: string;
  settings: any;
  formattedExportedDate: string;
  embeddedExperience: DashboardExperience;
  destroy$: Subject<any> = new Subject<any>();
  ngOnInit(): void {
    this.shopService.shop$
      .pipe(
        takeUntil(this.destroy$),
        switchMap((shop) => {
          this.settings = shop?.metadata?.customDashboard;
          const timeZone = shop.metadata?.ianaTimeZone;
          const exportedAtTime = this.settings?.lastExportedAt;
          if (exportedAtTime && timeZone) {
            const exportedTimeConvertedToTimeZone = moment(exportedAtTime).tz(timeZone);
            this.formattedExportedDate = exportedTimeConvertedToTimeZone.format('MMMM D h:mma, YYYY');
          }
          return this.apiService
            .get('quicksight/custom-dashboard-embedded-url')
            .pipe(map((result) => ({ result, shop })));
        }),
      )
      .subscribe(
        ({ result, shop }) => {
          if (!shop) {
            return;
          }

          if (!result?.url) {
            this.loading = false;
            this.notFoundMsg = 'Cannot find your custom dashboard url, try to reach our support';
            return;
          }
          this.initIframe(result.url);
        },
        (err) => {
          console.error(`Failed to get quicksight url ${err}`);
        },
      );
  }

  async initIframe(url: string) {
    this.minDate = moment().subtract(7, 'days').toDate();

    this.maxDate = moment().add(7, 'days').toDate();
    this.startDate = moment().subtract(1, 'days').toDate();
    this.endDate = moment().add(1, 'days').toDate();

    const embeddingContext = await createEmbeddingContext();

    const frameOptionsBuilder = new FrameOptionsBuilder(url, '#custom-dashboard-container')
      .withWidth('100%')
      .withOnChange((changeEvent, metadata) => {
        if ((changeEvent.eventName = 'CONTENT_LOADED')) {
          this.loading = false;
          const iframe = document.getElementsByTagName('iframe')[0];
          if (iframe) {
            iframe.style.border = '1px solid #e5e5e5';
          }
        }
      });

    const contentOptionsBuilder = new DashboardContentOptionsBuilder();

    const filterParameters = this.settings?.filterParameters;
    if (filterParameters && typeof filterParameters === 'object') {
      Object.entries(filterParameters).forEach(([key, value]) => {
        if (value != null) {
          contentOptionsBuilder.withParameter(key, String(value));
        }
      });
    } else {
      contentOptionsBuilder
        .withParameter('startDate', moment(this.startDate).toString())
        .withParameter('endDate', moment(this.endDate).toString());
    }

    const contentOptions = contentOptionsBuilder.build();
    const viewId = this.settings?.initialSheetId;
    if (viewId) {
      (contentOptions as any).viewId = viewId;
    }

    this.embeddedExperience = await embeddingContext.embedDashboard(frameOptionsBuilder.build(), contentOptions);
  }

  setDateRangeValue(value: { start: Moment; end: Moment }) {
    const { start, end } = value;
    if (!start || !end) {
      return;
    }

    this.embeddedExperience.setParameters([
      {
        Name: 'startDate',
        Values: [start.toString()],
      },
      {
        Name: 'endDate',
        Values: [end.toString()],
      },
    ]);
  }

  onCsvChange(file: File) {
    this.csvFile = file;
    this.cdr.detectChanges();
  }

  uploadCsvFile() {
    this.csvIsBeingUploaded = true;
    this.apiService.postFile('quicksight/upload-quicksight-csv-export', this.csvFile).subscribe(
      (response) => this.uploadFileSuccess(response?.numberOfOrders),
      (error) => this.uploadFileFail(),
    );
  }

  uploadFileSuccess(numberOfOrders: number) {
    this.csvIsBeingUploaded = false;
    this.csvFile = undefined;
    this.toastService.show(PdqToastComponent, {
      position: 'top',
      message: numberOfOrders + ' orders successfully marked as exported.',
      mode: 'success',
      duration: 10 * TimeUnits._SECOND_IN_MS,
    });
    setTimeout(() => {
      this.refreshPage();
    }, 2000);
  }

  uploadFileFail() {
    this.csvIsBeingUploaded = false;
    this.toastService.show(PdqToastComponent, {
      position: 'top',
      message: 'Failed to mark orders as processed. If the issue persists, please contact PDQ Support',
      mode: 'danger',
      duration: 5 * TimeUnits._SECOND_IN_MS,
    });
  }

  refreshPage() {
    window.location.reload();
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
  }
}
